import React, {useState, useMemo, useEffect} from 'react';
import {NavLink, useParams, useNavigate, useLocation} from 'react-router-dom';
import {connect} from 'react-redux';
import {toast} from 'react-toastify';
import {Accordion, Checkbox, PrimaryButton} from '../../../components';
import Loader from '../../../components/Loader';
import * as Actions from '../../../store/actions';
import {plusGrey} from '../../../assets/images/images';
import DashboardLayout from '../../../components/DashboardLayout';
import {Constants} from '../../../helpers';
import {PAGES_URL} from '../../../helpers/constants';
import Breadcrumbs from 'components/BreadCrumbs';
import ProfiledRiskRoles from 'routes/ProfiledRisk/ProfiledRiskRoles';

function Permission({permission, disabled = false, ...rest}) {
  return (
    <div
      className={`flex items-center ${disabled ? 'cursor-not-allowed' : ''}`}
    >
      <Checkbox noLeftPadding {...rest} disabled={disabled} />
      <span
        title={permission}
        className={`text-sm truncated-text
      ${disabled ? 'text-grey30' : 'text-body'}
      `}
      >
        {permission}
      </span>
    </div>
  );
}

const SettingsTeamLayout = ({
  children,
  team: {
    roles: existingRoles,
    loading,
    editTeamRoleLoading,
    deleteTeamRoleLoading,
  },
  editTeamRole,
  addTeamRole,
  deleteTeamRole,
  auth: {userDetails},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const allRoles = useMemo(
    () =>
      existingRoles
        ?.filter(r => r.role !== 'Owner')
        ?.map(r => ({
          role: r.role,
          path: `/settings/team/${r.role.toLowerCase().replaceAll(' ', '-')}`,
          id: r.id,
          reconciliationPermissions: r.reconciliationPermissions,
          customListPermissions: r.customListPermissions,
          lookupPermissions: r.lookupPermissions,
          settingsPermissions: r.settingsPermissions,
          onboardPermissions: r.onboardPermissions,
          amlScreeningPermissions: r.amlScreeningPermissions,
          detectPermissions: r.detectPermissions,
          quickActionPermissions: r.quickActionPermissions,
          addressVerificationPermissions: r.addressVerificationPermissions,
          billingPermissions: r.billingPermissions,
          baasPermissions: r.baasPermissions,
          documentAnalysisPermissions: r.documentAnalysisPermissions,
          developerPermissions: r.developerPermissions,
          analyticsPermissions: r.analyticsPermissions,
          profiledRiskPermissions: r.profiledRiskPermissions,
        })),
    [existingRoles],
  );

  const [roles, setRoles] = useState(allRoles);
  const [role, setRole] = useState('');

  const isProfiledRisk = localStorage
    ?.getItem('subdomain')
    ?.includes('profiledrisk');

  const [analyticsPermissions, setAnalyticsPermissions] = useState({});
  const [allAnalyticsPermissions, setAllAnalyticsPermissions] = useState(false);
  const [customListPermissions, setCustomListPermissions] = useState({});
  const [allCustomListPermissions, setAllCustomListPermissions] =
    useState(false);
  const [reconciliationPermissions, setReconciliationPermissions] = useState(
    {},
  );
  const [allReconciliationPermissions, setAllReconciliationPermissions] =
    useState(false);
  const [lookupPermissions, setLookupPermissions] = useState({});
  const [allLookupPermissions, setAllLookupPermissions] = useState(false);
  const [settingsPermissions, setSettingsPermissions] = useState({});
  const [allSettingsPermissions, setAllSettingsPermissions] = useState(false);
  const [onboardPermissions, setOnboardPermissions] = useState({});
  const [allOnboardPermissions, setAllOnboardPermissions] = useState(false);
  const [billingPermissions, setBillingPermissions] = useState({});
  const [baasPermissions, setBAASPermissions] = useState({});
  const [allBaasPermissions, setAllBaasPermissions] = useState(false);
  const [allBillingPermissions, setAllBillingPermissions] = useState(false);
  const [amlScreeningPermissions, setAmlScreeningPermissions] = useState({});
  const [allAmlScreeningPermissions, setAllAmlScreeningPermissions] =
    useState(false);

  const [baasTransactions, setBAASTransactions] = useState(false);
  const [baasWallet, setBAASWallet] = useState(false);
  const [baasPayout, setBAASPayout] = useState(false);

  const [apiReport, setApiReport] = useState(false);
  const [messagingLogs, setMessagingLogs] = useState(false);
  const [detectReport, setDetectReport] = useState(false);

  const [userScreening, setUserScreening] = useState(false);
  const [businessScreening, setBusinessScreening] = useState(false);

  const [developerPermissions, setDeveloperPermissions] = useState({});
  const [allDeveloperPermissions, setAllDeveloperPermissions] = useState(false);
  const [documentAnalysisPermissions, setDocumentAnalysisPermissions] =
    useState({});
  const [allDocumentAnalysisPermissions, setAllDocumentAnalysisPermissions] =
    useState(false);
  const [detectPermissions, setDetectPermissions] = useState({});
  const [allDetectPermissions, setAllDetectPermissions] = useState(false);
  const [quickActionPermissions, setQuickActionPermissions] = useState({});
  const [allQuickActionPermissions, setAllQuickActionPermissions] =
    useState(false);
  const [addressVerificationPermissions, setAddressVerificationPermissions] =
    useState({});
  const [
    allAddressVerificationPermissions,
    setAllAddressVerificationPermissions,
  ] = useState(false);
  const [allPermissions, setAllPermissions] = useState(false);

  const [profiledRiskPermissions, setProfiledRiskPermissions] = useState({});
  const [allProfiledRiskPermissions, setAllProfiledRiskPermissions] =
    useState(false);
  const [eventsGroup, setEventsGroup] = useState(false);
  const [manageFlowsGroup, setManageFlowsGroup] = useState(false);
  const [casesGroup, setCasesGroup] = useState(false);

  const [individualLookup, setIndividualLookup] = useState(false);
  const [businessLookup, setBusinessLookup] = useState(false);

  const {slug} = useParams();

  const currentRole = useMemo(
    () =>
      allRoles?.find(r => r.role.toLowerCase().replaceAll(' ', '-') === slug),
    [slug, allRoles],
  );

  const untitledRoleExists = useMemo(
    () => allRoles?.some(r => r.role === 'Untitled'),
    [allRoles],
  );

  const USER_COMPANY = userDetails && userDetails?.company;
  const {
    baas,
    address_verification_services,
    aml_screening_services,
    api_report_services,
    audit_logs_services,
    api_logs_services,
    billings_services,
    developers_services,
    easy_detect_report_services,
    easy_lookup_services,
    easy_onboard_services,
    quick_actions_services,
    teams_services,
    custom_link,
    reconciliation,
    messaging_report_services,
    document_analysis,
    easy_detect_services,
    // eslint-disable-next-line no-unsafe-optional-chaining
  } = USER_COMPANY?.modularisation || {};

  useEffect(() => {
    if (location.state && location.state.triggerAddRole) {
      createNewRole();
      navigate(location.pathname, {replace: true, state: {}});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (currentRole) {
      setRole(currentRole.role);
      setAnalyticsPermissions(currentRole.analyticsPermissions);
      setReconciliationPermissions(currentRole.reconciliationPermissions);
      setLookupPermissions(currentRole.lookupPermissions);
      setSettingsPermissions(currentRole.settingsPermissions);
      setOnboardPermissions(currentRole.onboardPermissions);
      setAmlScreeningPermissions(currentRole.amlScreeningPermissions);
      setDetectPermissions(currentRole.detectPermissions);
      setQuickActionPermissions(currentRole.quickActionPermissions);
      setAddressVerificationPermissions(
        currentRole.addressVerificationPermissions,
      );
      setBillingPermissions(currentRole.billingPermissions);
      setBAASPermissions(currentRole.baasPermissions);
      setDocumentAnalysisPermissions(currentRole.documentAnalysisPermissions);
      setDeveloperPermissions(currentRole.developerPermissions);
      setCustomListPermissions(currentRole.customListPermissions);
      setProfiledRiskPermissions(currentRole.profiledRiskPermissions);
    }
  }, [currentRole]);
  useEffect(() => {
    if (analyticsPermissions) {
      setAllAnalyticsPermissions(
        Object.values(analyticsPermissions).every(v => v),
      );
    }
  }, [analyticsPermissions]);
  useEffect(() => {
    if (customListPermissions) {
      setAllCustomListPermissions(
        Object.values(customListPermissions).every(v => v),
      );
    }
  }, [customListPermissions]);
  useEffect(() => {
    if (reconciliationPermissions) {
      setAllReconciliationPermissions(
        Object.values(reconciliationPermissions).every(v => v),
      );
    }
  }, [reconciliationPermissions]);
  useEffect(() => {
    if (developerPermissions) {
      setAllDeveloperPermissions(
        Object.values(developerPermissions).every(v => v),
      );
    }
  }, [developerPermissions]);
  useEffect(() => {
    if (documentAnalysisPermissions) {
      setAllDocumentAnalysisPermissions(
        Object.values(documentAnalysisPermissions).every(v => v),
      );
    }
  }, [documentAnalysisPermissions]);
  useEffect(() => {
    if (billingPermissions) {
      setAllBillingPermissions(Object.values(billingPermissions).every(v => v));
    }
  }, [billingPermissions]);
  useEffect(() => {
    if (baasPermissions) {
      setAllBaasPermissions(Object.values(baasPermissions).every(v => v));
    }
  }, [baasPermissions]);
  useEffect(() => {
    if (lookupPermissions) {
      setAllLookupPermissions(Object.values(lookupPermissions).every(v => v));
      setIndividualLookup(
        lookupPermissions.performSingleIndividualLookup &&
          lookupPermissions.viewSingleIndividualLookup &&
          lookupPermissions.downloadIndividualLookup &&
          lookupPermissions.performBatchIndividualLookup &&
          lookupPermissions.viewBatchIndividualLookup,
      );
      setBusinessLookup(
        lookupPermissions.performSingleBusinessLookup &&
          lookupPermissions.viewSingleBusinessLookup &&
          lookupPermissions.downloadBusinessLookup &&
          lookupPermissions.performBatchBusinessLookup &&
          lookupPermissions.viewBatchBusinessLookup,
      );
    }
  }, [lookupPermissions]);
  useEffect(() => {
    if (addressVerificationPermissions) {
      setAllAddressVerificationPermissions(
        Object.values(addressVerificationPermissions).every(v => v),
      );
    }
  }, [addressVerificationPermissions]);
  useEffect(() => {
    setAllSettingsPermissions(Object.values(settingsPermissions).every(v => v));
  }, [settingsPermissions]);
  useEffect(() => {
    if (onboardPermissions) {
      setAllOnboardPermissions(Object.values(onboardPermissions).every(v => v));
    }
  }, [onboardPermissions]);
  useEffect(() => {
    if (amlScreeningPermissions) {
      setAllAmlScreeningPermissions(
        Object.values(amlScreeningPermissions).every(v => v),
      );
      setUserScreening(
        amlScreeningPermissions.performSingleIndividualLookup &&
          amlScreeningPermissions.viewSingleIndividualLookup &&
          amlScreeningPermissions.performBatchIndividualLookup &&
          amlScreeningPermissions.viewBatchIndividualLookup,
      );
      setBusinessScreening(
        amlScreeningPermissions.performSingleBusinessLookup &&
          amlScreeningPermissions.viewSingleBusinessLookup &&
          amlScreeningPermissions.performBatchBusinessLookup &&
          amlScreeningPermissions.viewBatchBusinessLookup,
      );
    }
  }, [amlScreeningPermissions]);
  useEffect(() => {
    if (analyticsPermissions) {
      setAllAnalyticsPermissions(
        Object.values(analyticsPermissions).every(v => v),
      );
      setApiReport(
        analyticsPermissions.viewApi && analyticsPermissions.exportApi,
      );
      setMessagingLogs(
        analyticsPermissions.viewMessaging &&
          analyticsPermissions.exportMessaging,
      );
      setDetectReport(
        analyticsPermissions.viewEasyDetect &&
          analyticsPermissions.exportEasyDetect,
      );
    }
  }, [analyticsPermissions]);
  useEffect(() => {
    if (detectPermissions) {
      setAllDetectPermissions(Object.values(detectPermissions).every(v => v));
    }
  }, [detectPermissions]);
  useEffect(() => {
    if (quickActionPermissions) {
      setAllQuickActionPermissions(
        Object.values(quickActionPermissions).every(v => v),
      );
    }
  }, [quickActionPermissions]);
  useEffect(() => {
    if (profiledRiskPermissions) {
      const allProfiledRiskChecked = Object.values(
        profiledRiskPermissions,
      ).every(v => v);
      setAllProfiledRiskPermissions(allProfiledRiskChecked);

      setEventsGroup(profiledRiskPermissions.viewEventDetails);

      setManageFlowsGroup(
        profiledRiskPermissions.createFlows &&
          profiledRiskPermissions.deleteFlows &&
          profiledRiskPermissions.manageDecision &&
          profiledRiskPermissions.manageRules &&
          profiledRiskPermissions.viewCases &&
          profiledRiskPermissions.viewEvents &&
          profiledRiskPermissions.accessFlowSettings,
      );

      setCasesGroup(
        profiledRiskPermissions.reviewCases &&
          profiledRiskPermissions.downloadReports,
      );
    }
  }, [profiledRiskPermissions]);
  useEffect(() => {
    if (baasPermissions) {
      setAllBaasPermissions(Object.values(baasPermissions).every(v => v));
      setBAASTransactions(
        baasPermissions?.transfer &&
          baasPermissions?.viewTransactions &&
          baasPermissions?.exportTransactions,
      );
      setBAASWallet(
        baasPermissions?.viewWallets && baasPermissions?.exportWallets,
      );
      setBAASPayout(
        baasPermissions?.payout &&
          baasPermissions?.viewPayouts &&
          baasPermissions?.exportPayouts,
      );
    }
  }, [baasPermissions]);

  useEffect(() => {
    setAllPermissions(
      [
        allLookupPermissions,
        allOnboardPermissions,
        allSettingsPermissions,
        allAmlScreeningPermissions,
        allDetectPermissions,
        allQuickActionPermissions,
        allAddressVerificationPermissions,
        allBillingPermissions,
        allBaasPermissions,
        allDocumentAnalysisPermissions,
        allDeveloperPermissions,
        allReconciliationPermissions,
        allCustomListPermissions,
        allAnalyticsPermissions,
        allProfiledRiskPermissions,
        userScreening,
        businessScreening,
        individualLookup,
        businessLookup,
        apiReport,
        messagingLogs,
        detectReport,
        baasTransactions,
        baasWallet,
        baasPayout,
      ].every(v => v),
    );
  }, [
    allLookupPermissions,
    allOnboardPermissions,
    allSettingsPermissions,
    allAmlScreeningPermissions,
    allDetectPermissions,
    allQuickActionPermissions,
    allAddressVerificationPermissions,
    allBillingPermissions,
    allBaasPermissions,
    allDocumentAnalysisPermissions,
    allDeveloperPermissions,
    allReconciliationPermissions,
    allCustomListPermissions,
    allAnalyticsPermissions,
    allProfiledRiskPermissions,
    userScreening,
    businessScreening,
    individualLookup,
    businessLookup,
    apiReport,
    messagingLogs,
    detectReport,
    baasTransactions,
    baasWallet,
    baasPayout,
  ]);

  const createNewRole = (roleName = 'Untitled') => {
    if (untitledRoleExists)
      return toast.error("You cannot have multiple 'Untitled' roles");
    addTeamRole({role: roleName}, updatedRoles =>
      updateRoles(updatedRoles, roleName),
    );
  };
  const deleteRole = () => {
    deleteTeamRole(currentRole.id, updatedRoles =>
      updateRoles(updatedRoles, 'Owner'),
    );
  };
  const updateRole = () => {
    if (role === 'Untitled' || role === '') {
      toast.error(
        "Role name cannot be 'Untitled' or blank. Please choose a different name.",
      );
      return;
    }
    if (
      allRoles?.some(
        r =>
          r.role.toLowerCase() === role.toLowerCase() &&
          r.id !== currentRole.id,
      )
    ) {
      toast.error('Role name already exists. Please choose a different name.');
      return;
    }
    delete billingPermissions.viewAndExportLogs;
    editTeamRole(
      currentRole.id,
      {
        role,
        reconciliationPermissions,
        lookupPermissions,
        settingsPermissions,
        onboardPermissions,
        amlScreeningPermissions,
        detectPermissions,
        quickActionPermissions,
        addressVerificationPermissions,
        billingPermissions,
        baasPermissions,
        documentAnalysisPermissions,
        developerPermissions,
        customListPermissions,
        analyticsPermissions,
        profiledRiskPermissions,
      },
      updatedRoles => updateRoles(updatedRoles, role),
    );
  };
  const updateRoles = (updatedRoles, role) => {
    const newPath = `/settings/team/${role.toLowerCase().replaceAll(' ', '-')}`;
    setRoles(
      updatedRoles
        ?.filter(r => r.role !== 'Owner')
        ?.map(r => ({
          role: r.role,
          path: `/settings/team/${r.role.toLowerCase().replaceAll(' ', '-')}`,
          id: r.id,
          reconciliationPermissions: r.reconciliationPermissions,
          lookupPermissions: r.lookupPermissions,
          settingsPermissions: r.settingsPermissions,
          onboardPermissions: r.onboardPermissions,
          amlScreeningPermissions: r.amlScreeningPermissions,
          detectPermissions: r.detectPermissions,
          quickActionPermissions: r.quickActionPermissions,
          addressVerificationPermissions: r.addressVerificationPermissions,
          billingPermissions: r.billingPermissions,
          documentAnalysisPermissions: r.documentAnalysisPermissions,
          developerPermissions: r.developerPermissions,
          customListPermissions: r.customListPermissions,
          analyticsPermissions: r.analyticsPermissions,
          baasPermissions: r.baasPermissions,
          profiledRiskPermissions: r.profiledRiskPermissions,
        })),
    );
    navigate(newPath);
  };

  const changeAnalyticsPermissions = permission => {
    setAnalyticsPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };

  const toggleAllProfiledRiskPagePermissions = () => {
    const allChecked =
      allProfiledRiskPermissions &&
      allBillingPermissions &&
      allCustomListPermissions &&
      allDeveloperPermissions;
    const newOption = !allChecked;

    toggleProfiledRiskPermissions(newOption);
    toggleBillingPermissions(newOption);
    toggleCustomListPermissions(newOption);
    toggleDeveloperPermissions(newOption);
  };

  const changeProfiledRiskPermissions = permission => {
    setProfiledRiskPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }
      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };

  const toggleProfiledRiskPermissions = option => {
    const newOption = option ?? !allProfiledRiskPermissions;
    setProfiledRiskPermissions(prevState => {
      if (prevState) {
        const newState = {...prevState};
        Object.keys(newState).forEach(key => {
          newState[key] = newOption;
        });
        return newState;
      }
      return {};
    });
    setAllProfiledRiskPermissions(newOption);
    setEventsGroup(newOption);
    setManageFlowsGroup(newOption);
    setCasesGroup(newOption);
  };

  const toggleEventsGroup = option => {
    const newOption = option ?? !eventsGroup;
    setEventsGroup(newOption);
    setProfiledRiskPermissions(prevState => ({
      ...prevState,
      viewEventDetails: newOption,
    }));
  };

  const toggleManageFlowsGroup = option => {
    const newOption = option ?? !manageFlowsGroup;
    setManageFlowsGroup(newOption);
    setProfiledRiskPermissions(prevState => ({
      ...prevState,
      createFlows: newOption,
      deleteFlows: newOption,
      manageDecision: newOption,
      manageRules: newOption,
      viewCases: newOption,
      viewEvents: newOption,
      accessFlowSettings: newOption,
    }));
  };

  const toggleCasesGroup = option => {
    const newOption = option ?? !casesGroup;
    setCasesGroup(newOption);
    setProfiledRiskPermissions(prevState => ({
      ...prevState,
      reviewCases: newOption,
      downloadReports: newOption,
    }));
  };
  const changeCustomListPermissions = permission => {
    setCustomListPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeReconciliationPermissions = permission => {
    setReconciliationPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeDeveloperPermissions = permission => {
    setDeveloperPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeDocumentAnalysisPermissions = permission => {
    setDocumentAnalysisPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeBillingPermissions = permission => {
    setBillingPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeBaasPermissions = permission => {
    setBAASPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const toggleCustomListPermissions = option => {
    setCustomListPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allCustomListPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllCustomListPermissions(option ?? !allCustomListPermissions);
  };
  const toggleReconciliationPermissions = option => {
    setReconciliationPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allReconciliationPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllReconciliationPermissions(option ?? !allReconciliationPermissions);
  };
  const toggleDocumentAnalysisPermissions = option => {
    setDocumentAnalysisPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allDocumentAnalysisPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllDocumentAnalysisPermissions(
      option ?? !allDocumentAnalysisPermissions,
    );
  };
  const toggleBillingPermissions = option => {
    setBillingPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allBillingPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllBillingPermissions(option ?? !allBillingPermissions);
  };

  const toggleDeveloperPermissions = option => {
    setDeveloperPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allDeveloperPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllDeveloperPermissions(option ?? !allDeveloperPermissions);
  };
  const toggleLookupPermissions = option => {
    const newOption = option ?? !allAmlScreeningPermissions;
    setLookupPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = newOption;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllLookupPermissions(newOption);
    toggleIndividualLookup(newOption);
    toggleBusinessLookup(newOption);
  };
  const toggleIndividualLookup = option => {
    setIndividualLookup(option);
    setLookupPermissions(prevState => ({
      ...prevState,
      performSingleIndividualLookup: option,
      viewSingleIndividualLookup: option,
      downloadIndividualLookup: option,
      performBatchIndividualLookup: option,
      viewBatchIndividualLookup: option,
    }));
  };
  const toggleBusinessLookup = option => {
    setBusinessLookup(option);
    setLookupPermissions(prevState => ({
      ...prevState,
      performSingleBusinessLookup: option,
      viewSingleBusinessLookup: option,
      downloadBusinessLookup: option,
      performBatchBusinessLookup: option,
      viewBatchBusinessLookup: option,
    }));
  };

  const toggleSettingsPermissions = option => {
    setSettingsPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allSettingsPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllSettingsPermissions(option ?? !allSettingsPermissions);
  };
  const toggleAddressVerificationPermissions = option => {
    setAddressVerificationPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allAddressVerificationPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllAddressVerificationPermissions(
      option ?? !allAddressVerificationPermissions,
    );
  };
  const toggleOnboardPermissions = option => {
    setOnboardPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allOnboardPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllOnboardPermissions(option ?? !allOnboardPermissions);
  };
  const toggleAmlScreeningPermissions = option => {
    const newOption = option ?? !allAmlScreeningPermissions;
    setAmlScreeningPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = newOption;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllAmlScreeningPermissions(newOption);
    toggleUserScreening(newOption);
    toggleBusinessScreening(newOption);
  };

  const toggleAnalyticsPermissions = option => {
    const newOption = option ?? !allAnalyticsPermissions;
    setAnalyticsPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = newOption;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllAnalyticsPermissions(newOption);
    toggleApiReport(newOption);
    toggleMessagingLogs(newOption);
    toggleDetectReport(newOption);
  };
  const toggleApiReport = option => {
    setApiReport(option);
    setAnalyticsPermissions(prevState => ({
      ...prevState,
      viewApi: option,
      exportApi: option,
    }));
  };
  const toggleMessagingLogs = option => {
    setMessagingLogs(option);
    setAnalyticsPermissions(prevState => ({
      ...prevState,
      viewMessaging: option,
      exportMessaging: option,
    }));
  };
  const toggleDetectReport = option => {
    setDetectReport(option);
    setAnalyticsPermissions(prevState => ({
      ...prevState,
      viewEasyDetect: option,
      exportEasyDetect: option,
    }));
  };
  const toggleUserScreening = option => {
    setUserScreening(option);
    setAmlScreeningPermissions(prevState => ({
      ...prevState,
      performSingleIndividualLookup: option,
      viewSingleIndividualLookup: option,
      performBatchIndividualLookup: option,
      viewBatchIndividualLookup: option,
    }));
  };
  const toggleBusinessScreening = option => {
    setBusinessScreening(option);
    setAmlScreeningPermissions(prevState => ({
      ...prevState,
      performSingleBusinessLookup: option,
      viewSingleBusinessLookup: option,
      performBatchBusinessLookup: option,
      viewBatchBusinessLookup: option,
    }));
  };

  const toggleBAASPermissions = option => {
    const newOption = option ?? !allBaasPermissions;
    setBAASPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = newOption;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllBaasPermissions(newOption);
    toggleBAASTransactions(newOption);
    toggleBAASWallet(newOption);
    toggleBAASPayout(newOption);
  };
  const toggleBAASTransactions = option => {
    setBAASTransactions(option);
    setBAASPermissions(prevState => ({
      ...prevState,
      transfer: option,
      viewTransactions: option,
      exportTransactions: option,
    }));
  };
  const toggleBAASWallet = option => {
    setBAASWallet(option);
    setBAASPermissions(prevState => ({
      ...prevState,
      viewWallets: option,
      exportWallets: option,
    }));
  };
  const toggleBAASPayout = option => {
    setBAASPayout(option);
    setBAASPermissions(prevState => ({
      ...prevState,
      payout: option,
      viewPayouts: option,
      exportPayouts: option,
    }));
  };

  const toggleDetectPermissions = option => {
    setDetectPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allDetectPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllDetectPermissions(option ?? !allDetectPermissions);
  };
  const toggleQuickActionPermissions = option => {
    setQuickActionPermissions(prevState => {
      if (prevState) {
        Object.keys(prevState).forEach(key => {
          prevState[key] = option ?? !allQuickActionPermissions;
        });
        return prevState;
      } else {
        return {};
      }
    });
    setAllQuickActionPermissions(option ?? !allQuickActionPermissions);
  };
  const changeDetectPermissions = permission => {
    setDetectPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeQuickActionPermissions = permission => {
    setQuickActionPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const toggleAllPermissions = () => {
    toggleReconciliationPermissions(!allPermissions);
    toggleLookupPermissions(!allPermissions);
    toggleSettingsPermissions(!allPermissions);
    toggleOnboardPermissions(!allPermissions);
    toggleAmlScreeningPermissions(!allPermissions);
    toggleDetectPermissions(!allPermissions);
    toggleQuickActionPermissions(!allPermissions);
    toggleAddressVerificationPermissions(!allPermissions);
    toggleBillingPermissions(!allPermissions);
    toggleBAASPermissions(!allPermissions);
    toggleDocumentAnalysisPermissions(!allPermissions);
    toggleCustomListPermissions(!allPermissions);
    toggleAnalyticsPermissions(!allPermissions);
    toggleDeveloperPermissions(!allPermissions);
    toggleProfiledRiskPermissions(!allPermissions);
    toggleUserScreening(!allPermissions);
    toggleBusinessScreening(!allPermissions);
    toggleBusinessLookup(!allPermissions);
    toggleIndividualLookup(!allPermissions);
    toggleApiReport(!allPermissions);
    toggleBAASTransactions(!allPermissions);
    toggleMessagingLogs(!allPermissions);
    toggleDetectReport(!allPermissions);
  };
  const changeLookupPermissions = permission => {
    setLookupPermissions(prevState => {
      if (
        permission === 'performIndividualLookupAndViewHistory' ||
        permission === 'performBatchLookupAndViewHistory'
      ) {
        // Toggle performIndividualLookupAndViewHistory and downloadIndividualHistory together
        // Toggle performBatchLookupAndViewHistory and downloadBatchHistory together
        const performLookup = !prevState[permission];
        const downloadHistoryPermission =
          permission === 'performIndividualLookupAndViewHistory'
            ? 'downloadIndividualHistory'
            : 'downloadBatchHistory';
        const downloadHistory = performLookup
          ? prevState[downloadHistoryPermission]
          : false;
        return {
          ...prevState,
          [permission]: performLookup,
          [downloadHistoryPermission]: downloadHistory,
        };
      } else if (
        permission === 'downloadIndividualHistory' &&
        !prevState.performIndividualLookupAndViewHistory
      ) {
        // If downloadIndividualHistory is checked and performIndividualLookupAndViewHistory is unchecked, uncheck downloadIndividualHistory
        return {
          ...prevState,
          downloadIndividualHistory: false,
        };
      } else if (
        permission === 'downloadBatchHistory' &&
        !prevState.performBatchLookupAndViewHistory
      ) {
        // If downloadBatchHistory is checked and performBatchLookupAndViewHistory is unchecked, uncheck downloadBatchHistory
        return {
          ...prevState,
          downloadBatchHistory: false,
        };
      } else {
        // Otherwise, toggle the permission as usual
        return {
          ...prevState,
          [permission]: !prevState[permission],
        };
      }
    });
  };
  const changeSettingsPermissions = permission => {
    setSettingsPermissions(prevState => {
      if (permission === 'viewAudit') {
        // Toggle viewAudit and downloadAudit together
        const viewAudit = !prevState[permission];
        const downloadAudit = viewAudit ? prevState.downloadAudit : false;
        return {
          ...prevState,
          [permission]: viewAudit,
          downloadAudit: downloadAudit,
        };
      } else if (permission === 'downloadAudit' && !prevState.viewAudit) {
        // If downloadAudit is checked and viewAudit is unchecked, uncheck downloadAudit
        return {
          ...prevState,
          downloadAudit: false,
        };
      } else {
        // Otherwise, toggle the permission as usual
        return {
          ...prevState,
          [permission]: !prevState[permission],
        };
      }
    });
  };
  const changeOnboardPermissions = permission => {
    setOnboardPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeAddressVerificationPermissions = permission => {
    setAddressVerificationPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };
  const changeAmlScreeningPermissions = permission => {
    setAmlScreeningPermissions(prevState => {
      if (!prevState) {
        return {
          [permission]: true,
        };
      }

      return {
        ...prevState,
        [permission]: !prevState[permission],
      };
    });
  };

  return (
    <DashboardLayout
      bg="bg-white80"
      breadCrumbs={<p className="text-tiny font-normal text-deepBlue">Team</p>}
    >
      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {label: 'Team', path: PAGES_URL.SETTINGS_TEAM},
            {label: 'Manage Roles & Permissions'},
          ]}
        />
      </div>

      <div className="mt-6">
        <div className="rounded-md bg-[#f1f1f1] w-fit p-1 max-w-[98%] flex items-center gap-2 overflow-x-auto">
          {' '}
          <NavLink
            end
            to={Constants.PAGES_URL.SETTINGS_TEAM_OWNER}
            className={({isActive}) =>
              `px-7 py-1.5 min-h-8 text-sm  flex items-center justify-center rounded-lg shadow-[0px_4px_32px_-4px,rgba(199,196,196,0.18)] ${
                isActive
                  ? 'text-black bg-white shadow-tabs'
                  : 'text-grey-500 bg-transparent'
              }`
            }
          >
            Owner
          </NavLink>
          {roles?.map(({role, path, id}) => (
            <NavLink
              key={id}
              end
              to={path}
              className={({isActive}) =>
                `px-7 py-1.5 min-h-8 text-sm whitespace-nowrap shrink-0 flex items-center justify-center rounded-lg shadow-[0px_4px_32px_-4px,rgba(199,196,196,0.18)] ${
                  isActive
                    ? 'text-black bg-white shadow-tabs'
                    : 'text-grey-500 bg-transparent'
                }`
              }
            >
              {role}
            </NavLink>
          ))}
          <button
            className="flex items-center min-w-[140px] gap-1 px-7 py-1.5 h-full"
            type="button"
            onClick={() => createNewRole()}
          >
            <img src={plusGrey} alt="" width={12} height={12} />
            <span className="text-xs font-medium text-grey30">
              Add new role
            </span>
          </button>
        </div>
      </div>

      {loading ? (
        <Loader height={50} />
      ) : currentRole ? (
        <section>
          <div className="flex items-center mt-3 py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
            <input
              type="text"
              className="lg:w-[400px] h-[36px]"
              id={currentRole.id}
              onChange={e => setRole(e.target.value)}
              value={role}
              placeholder="Role Name"
            />
            <div className="flex items-center gap-2">
              <div
                className="flex items-center gap-4"
                style={{...(!slug && {visibility: 'hidden'})}}
              >
                <PrimaryButton
                  buttonText="Delete role"
                  onClick={deleteRole}
                  loading={deleteTeamRoleLoading}
                  color="#F15D5F"
                  background="#FFFFFF"
                />
                <PrimaryButton
                  buttonText="Save changes"
                  onClick={updateRole}
                  loading={editTeamRoleLoading}
                />
              </div>
            </div>
          </div>

          {isProfiledRisk ? (
            <ProfiledRiskRoles
              allProfiledRiskPermissions={allProfiledRiskPermissions}
              toggleProfiledRiskPermissions={toggleProfiledRiskPermissions}
              profiledRiskPermissions={profiledRiskPermissions}
              eventsGroup={eventsGroup}
              manageFlowsGroup={manageFlowsGroup}
              casesGroup={casesGroup}
              changeProfiledRiskPermissions={changeProfiledRiskPermissions}
              toggleEventsGroup={toggleEventsGroup}
              toggleManageFlowsGroup={toggleManageFlowsGroup}
              toggleCasesGroup={toggleCasesGroup}
              billingPermissions={billingPermissions}
              changeBillingPermissions={changeBillingPermissions}
              customListPermissions={customListPermissions}
              changeCustomListPermissions={changeCustomListPermissions}
              developerPermissions={developerPermissions}
              changeDeveloperPermissions={changeDeveloperPermissions}
              allBillingPermissions={allBillingPermissions}
              allCustomListPermissions={allCustomListPermissions}
              allDeveloperPermissions={allDeveloperPermissions}
              toggleAllProfiledRiskPagePermissions={
                toggleAllProfiledRiskPagePermissions
              }
            />
          ) : (
            <div className="mt-8">
              <div className="flex items-center">
                <Checkbox
                  noLeftPadding
                  isChecked={allPermissions}
                  onChange={toggleAllPermissions}
                />
                <span className="text-sm text-body">All Permissions</span>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 items-start gap-4 mt-4">
                <div className="flex flex-col gap-4">
                  {quick_actions_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allQuickActionPermissions}
                            onChange={() => toggleQuickActionPermissions()}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Quick Actions
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="Ability to Top-up and View Wallet"
                              isChecked={quickActionPermissions?.topup}
                              onChange={() =>
                                changeQuickActionPermissions('topup')
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}

                  {reconciliation && (
                    <div className="rounded p-4 bg-white">
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allReconciliationPermissions}
                            onChange={() =>
                              changeReconciliationPermissions('reconciliation')
                            }
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Reconciliation
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {custom_link && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allCustomListPermissions}
                            onChange={() => toggleCustomListPermissions()}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Custom List
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="View custom list"
                              isChecked={customListPermissions?.viewCustomList}
                              onChange={() =>
                                changeCustomListPermissions('viewCustomList')
                              }
                            />
                            <Permission
                              permission="Create and edit custom list"
                              isChecked={
                                customListPermissions?.manageCustomList
                              }
                              onChange={() =>
                                changeCustomListPermissions('manageCustomList')
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}

                  {easy_onboard_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allOnboardPermissions}
                            onChange={() => toggleOnboardPermissions()}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Easy Onboard
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="View Verification flows"
                              isChecked={onboardPermissions?.viewFlows}
                              onChange={() =>
                                changeOnboardPermissions('viewFlows')
                              }
                            />
                            <Permission
                              permission="Create and edit flows"
                              isChecked={onboardPermissions?.manageFlows}
                              onChange={() =>
                                changeOnboardPermissions('manageFlows')
                              }
                            />

                            <Permission
                              permission="View verification logs"
                              isChecked={onboardPermissions?.viewVerifications}
                              onChange={() =>
                                changeOnboardPermissions('viewVerifications')
                              }
                            />

                            <Permission
                              permission="Export Verifications"
                              isChecked={
                                onboardPermissions?.exportVerifications
                              }
                              onChange={() =>
                                changeOnboardPermissions('exportVerifications')
                              }
                            />
                            <Permission
                              permission="Review Verifications"
                              isChecked={
                                onboardPermissions?.reviewVerifications
                              }
                              onChange={() =>
                                changeOnboardPermissions('reviewVerifications')
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}

                  {easy_detect_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allDetectPermissions}
                            onChange={() => toggleDetectPermissions()}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Easy Detect
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="Manage flows"
                              isChecked={detectPermissions?.manageFlows}
                              onChange={() =>
                                changeDetectPermissions('manageFlows')
                              }
                            />
                            <Permission
                              permission="Edit Process"
                              isChecked={detectPermissions?.editProcess}
                              onChange={() =>
                                changeDetectPermissions('editProcess')
                              }
                            />
                            <Permission
                              permission="Review pending cases"
                              isChecked={detectPermissions?.reviewCases}
                              onChange={() =>
                                changeDetectPermissions('reviewCases')
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  {easy_lookup_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allLookupPermissions}
                            onChange={() => {
                              const newOption = !allLookupPermissions;
                              toggleLookupPermissions(newOption);
                              toggleIndividualLookup(newOption);
                              toggleBusinessLookup(newOption);
                            }}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Government Lookup
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="mt-6">
                          <div className="flex items-center">
                            <Checkbox
                              noLeftPadding
                              isChecked={individualLookup}
                              onChange={() =>
                                toggleIndividualLookup(!individualLookup)
                              }
                            />
                            <span className="text-xs font-medium uppercase text-grey">
                              Individual Lookup
                            </span>
                          </div>

                          <div className="flex flex-col gap-4 pl-5 mt-4">
                            <Permission
                              permission="Perform Individual single lookup"
                              isChecked={
                                lookupPermissions?.performSingleIndividualLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'performSingleIndividualLookup',
                                )
                              }
                            />
                            <Permission
                              permission="View Individual single history table"
                              isChecked={
                                lookupPermissions?.viewSingleIndividualLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'viewSingleIndividualLookup',
                                )
                              }
                            />
                            <Permission
                              permission="Export Individual single lookup"
                              isChecked={
                                lookupPermissions?.downloadIndividualLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'downloadIndividualLookup',
                                )
                              }
                              // disabled={
                              //   !lookupPermissions.performIndividualLookupAndViewHistory
                              // }
                            />
                            <Permission
                              permission="Perform Individual batch lookup"
                              isChecked={
                                lookupPermissions?.performBatchIndividualLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'performBatchIndividualLookup',
                                )
                              }
                              // disabled={
                              //   !lookupPermissions.performBatchLookupAndViewHistory
                              // }
                            />
                            <Permission
                              permission="View Individual batch history table"
                              isChecked={
                                lookupPermissions?.viewBatchIndividualLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'viewBatchIndividualLookup',
                                )
                              }
                              // disabled={
                              //   !lookupPermissions.performBatchLookupAndViewHistory
                              // }
                            />
                            <Permission
                              permission="Export Individual batch lookup"
                              isChecked={
                                lookupPermissions?.downloadIndividualLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'downloadIndividualLookup',
                                )
                              }
                              // disabled={!lookupPermissions.downloadIndividualLookup}
                            />
                          </div>
                        </div>
                        <div className="mt-6">
                          <div className="flex items-center">
                            <Checkbox
                              noLeftPadding
                              isChecked={businessLookup}
                              onChange={() =>
                                toggleBusinessLookup(!businessLookup)
                              }
                            />
                            <span className="text-xs font-medium uppercase text-grey">
                              Business Lookup
                            </span>
                          </div>

                          <div className="flex flex-col gap-4 pl-5 mt-4">
                            <Permission
                              permission="Perform Business single lookup"
                              isChecked={
                                lookupPermissions?.performSingleBusinessLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'performSingleBusinessLookup',
                                )
                              }
                            />
                            <Permission
                              permission="View Business single history table"
                              isChecked={
                                lookupPermissions?.viewSingleBusinessLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'viewSingleBusinessLookup',
                                )
                              }
                            />
                            <Permission
                              permission="Export Business single history table"
                              isChecked={
                                lookupPermissions?.downloadBusinessLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'downloadBusinessLookup',
                                )
                              }
                              // disabled={
                              //   !lookupPermissions.performSingleBusinessLookup
                              // }
                            />
                            <Permission
                              permission="Perform Business Batch lookup"
                              isChecked={
                                lookupPermissions?.performBatchBusinessLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'performBatchBusinessLookup',
                                )
                              }
                              // disabled={
                              //   !lookupPermissions.performBatchLookupAndViewHistory
                              // }
                            />
                            <Permission
                              permission="View Business Batch history table"
                              isChecked={
                                lookupPermissions?.viewBatchBusinessLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'viewBatchBusinessLookup',
                                )
                              }
                              // disabled={
                              //   !lookupPermissions.performBatchLookupAndViewHistory
                              // }
                            />
                            <Permission
                              permission="Export Business Batch history table"
                              isChecked={
                                lookupPermissions?.downloadBusinessLookup
                              }
                              onChange={() =>
                                changeLookupPermissions(
                                  'downloadBusinessLookup',
                                )
                              }
                              // disabled={
                              //   !lookupPermissions.performIndividualLookupAndViewHistory
                              // }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}

                  {billings_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allBillingPermissions}
                            onChange={() => toggleBillingPermissions()}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Billings
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="Top up"
                              isChecked={billingPermissions?.topup}
                              onChange={() => changeBillingPermissions('topup')}
                            />
                            <Permission
                              permission="Manage Threshold"
                              isChecked={billingPermissions?.manageThreshold}
                              onChange={() =>
                                changeBillingPermissions('manageThreshold')
                              }
                            />
                            <Permission
                              permission="View transaction history"
                              isChecked={billingPermissions?.viewHistory}
                              onChange={() =>
                                changeBillingPermissions('viewHistory')
                              }
                            />
                            <Permission
                              permission="Export transaction history"
                              isChecked={billingPermissions?.exportHistory}
                              onChange={() =>
                                changeBillingPermissions('exportHistory')
                              }
                            />
                            <Permission
                              permission="View API calls"
                              isChecked={billingPermissions?.viewLogs}
                              onChange={() =>
                                changeBillingPermissions('viewLogs')
                              }
                            />
                            <Permission
                              permission="Export API calls"
                              isChecked={billingPermissions?.exportLogs}
                              onChange={() =>
                                changeBillingPermissions('exportLogs')
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  {address_verification_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allAddressVerificationPermissions}
                            onChange={() =>
                              toggleAddressVerificationPermissions()
                            }
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Address verifications
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="Perform submit individual address"
                              isChecked={
                                addressVerificationPermissions?.performIndividualLookupAndViewHistory
                              }
                              onChange={() =>
                                changeAddressVerificationPermissions(
                                  'performIndividualLookupAndViewHistory',
                                )
                              }
                            />
                            <Permission
                              permission="Export individual address"
                              isChecked={
                                addressVerificationPermissions?.downloadIndividualHistory
                              }
                              onChange={() =>
                                changeAddressVerificationPermissions(
                                  'downloadIndividualHistory',
                                )
                              }
                            />
                            <Permission
                              permission="Perform submit batch address"
                              isChecked={
                                addressVerificationPermissions?.performBatchLookupAndViewHistory
                              }
                              onChange={() =>
                                changeAddressVerificationPermissions(
                                  'performBatchLookupAndViewHistory',
                                )
                              }
                            />
                            <Permission
                              permission="Export batch address"
                              isChecked={
                                addressVerificationPermissions?.downloadBatchHistory
                              }
                              onChange={() =>
                                changeAddressVerificationPermissions(
                                  'downloadBatchHistory',
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}

                  <Accordion
                    title={
                      <div className="flex items-center">
                        <Checkbox
                          noLeftPadding
                          isChecked={allAnalyticsPermissions}
                          onChange={() => {
                            const newOption = !allAnalyticsPermissions;
                            toggleAnalyticsPermissions(newOption);
                            toggleApiReport(newOption);
                            toggleMessagingLogs(newOption);
                            toggleDetectReport(newOption);
                          }}
                        />
                        <span className="text-xs font-medium uppercase text-grey">
                          Report Analytics
                        </span>
                      </div>
                    }
                  >
                    {api_report_services && (
                      <div className="mt-6">
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={apiReport}
                            onChange={() => toggleApiReport(!apiReport)}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Api Report
                          </span>
                        </div>
                        <div className="flex flex-col gap-4 pl-5 mt-4">
                          <Permission
                            permission="View API report"
                            isChecked={analyticsPermissions?.viewApi}
                            onChange={() =>
                              changeAnalyticsPermissions('viewApi')
                            }
                          />
                          <Permission
                            permission="Export API report"
                            isChecked={analyticsPermissions?.exportApi}
                            onChange={() =>
                              changeAnalyticsPermissions('exportApi')
                            }
                          />
                        </div>
                      </div>
                    )}

                    {messaging_report_services && (
                      <div className="mt-6">
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={messagingLogs}
                            onChange={() => toggleMessagingLogs(!messagingLogs)}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Messaging Logs
                          </span>
                        </div>
                        <div className="flex flex-col gap-4 pl-5 mt-4">
                          <Permission
                            permission="View messaging logs"
                            isChecked={analyticsPermissions?.viewMessaging}
                            onChange={() =>
                              changeAnalyticsPermissions('viewMessaging')
                            }
                          />
                          <Permission
                            permission="Export messaging logs"
                            isChecked={analyticsPermissions?.exportMessaging}
                            onChange={() =>
                              changeAnalyticsPermissions('exportMessaging')
                            }
                          />
                        </div>
                      </div>
                    )}

                    {easy_detect_report_services && (
                      <div className="mt-6">
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={detectReport}
                            onChange={() => toggleDetectReport(!detectReport)}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Easy Detect Report
                          </span>
                        </div>
                        <div className="flex flex-col gap-4 pl-5 mt-4">
                          <Permission
                            permission="View Easy Detect report"
                            isChecked={analyticsPermissions?.viewEasyDetect}
                            onChange={() =>
                              changeAnalyticsPermissions('viewEasyDetect')
                            }
                          />
                          <Permission
                            permission="Export easy detect"
                            isChecked={analyticsPermissions?.exportEasyDetect}
                            onChange={() =>
                              changeAnalyticsPermissions('exportEasyDetect')
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Accordion>

                  {baas && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <span className="text-xs font-medium uppercase text-grey"></span>
                          <Checkbox
                            noLeftPadding
                            isChecked={allBaasPermissions}
                            onChange={() => {
                              const newOption = !allBaasPermissions;
                              toggleBAASPermissions(newOption);
                              toggleBAASWallet(newOption);
                              toggleBAASPayout(newOption);
                            }}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            BAAS
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className=" mt-6 flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={baasPermissions?.overview}
                            onChange={() => changeBaasPermissions('overview')}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Overview
                          </span>
                        </div>

                        <div className="mt-6">
                          <div className="flex items-center">
                            <Checkbox
                              noLeftPadding
                              isChecked={baasTransactions}
                              onChange={() =>
                                toggleBAASTransactions(!baasTransactions)
                              }
                            />
                            <span className="text-xs font-medium uppercase text-grey">
                              Transactions
                            </span>
                          </div>
                          <div className="flex flex-col gap-4 pl-5 mt-4">
                            <Permission
                              permission="Transfer"
                              isChecked={baasPermissions?.transfer}
                              onChange={() => changeBaasPermissions('transfer')}
                            />
                            <Permission
                              permission="View History Table"
                              isChecked={baasPermissions?.viewTransactions}
                              onChange={data => {
                                changeBaasPermissions('viewTransactions');
                                !data.target.checked &&
                                  setBAASPermissions(prev => ({
                                    ...prev,
                                    exportTransactions: false,
                                  }));
                              }}
                            />
                            <Permission
                              permission="Export History Table"
                              isChecked={baasPermissions?.exportTransactions}
                              onChange={data => {
                                changeBaasPermissions('exportTransactions');
                                data.target.checked &&
                                  setBAASPermissions(prev => ({
                                    ...prev,
                                    viewTransactions: true,
                                  }));
                              }}
                            />
                          </div>
                        </div>
                        <div className="mt-6">
                          <div className="flex items-center">
                            <Checkbox
                              noLeftPadding
                              isChecked={baasWallet}
                              onChange={() => toggleBAASWallet(!baasWallet)}
                            />
                            <span className="text-xs font-medium uppercase text-grey">
                              Wallet
                            </span>
                          </div>
                          <div className="flex flex-col gap-4 pl-5 mt-4">
                            <Permission
                              permission="View History Table"
                              isChecked={baasPermissions?.viewWallets}
                              onChange={data => {
                                changeBaasPermissions('viewWallets');
                                !data.target.checked &&
                                  setBAASPermissions(prev => ({
                                    ...prev,
                                    exportWallets: false,
                                  }));
                              }}
                            />
                            <Permission
                              permission="Export History Table"
                              isChecked={baasPermissions?.exportWallets}
                              onChange={data => {
                                changeBaasPermissions('exportWallets');
                                data.target.checked &&
                                  setBAASPermissions(prev => ({
                                    ...prev,
                                    viewWallets: true,
                                  }));
                              }}
                            />
                          </div>
                        </div>
                        <div className="mt-6">
                          <div className="flex items-center">
                            <Checkbox
                              noLeftPadding
                              isChecked={baasPayout}
                              onChange={() => toggleBAASPayout(!baasPayout)}
                            />
                            <span className="text-xs font-medium uppercase text-grey">
                              Payout
                            </span>
                          </div>
                          <div className="flex flex-col gap-4 pl-5 mt-4">
                            <Permission
                              permission="Payout"
                              isChecked={baasPermissions?.payout}
                              onChange={() => changeBaasPermissions('payout')}
                            />
                            <Permission
                              permission="View History Table"
                              isChecked={baasPermissions?.viewPayouts}
                              onChange={data => {
                                changeBaasPermissions('viewPayouts');
                                !data.target.checked &&
                                  setBAASPermissions(prev => ({
                                    ...prev,
                                    exportPayouts: false,
                                  }));
                              }}
                            />
                            <Permission
                              permission="Export History Table"
                              isChecked={baasPermissions?.exportPayouts}
                              onChange={data => {
                                changeBaasPermissions('exportPayouts');
                                data.target.checked &&
                                  setBAASPermissions(prev => ({
                                    ...prev,
                                    viewPayouts: true,
                                  }));
                              }}
                            />
                          </div>
                        </div>

                        <div className=" mt-6 flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={baasPermissions?.manageSettings}
                            onChange={() =>
                              changeBaasPermissions('manageSettings')
                            }
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            BAAS SETTINGS
                          </span>
                        </div>
                      </div>
                    </Accordion>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  {document_analysis && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allDocumentAnalysisPermissions}
                            onChange={() => toggleDocumentAnalysisPermissions()}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Document analysis
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="Perform single analysis"
                              isChecked={
                                documentAnalysisPermissions?.performSingleLookup
                              }
                              onChange={() =>
                                changeDocumentAnalysisPermissions(
                                  'performSingleLookup',
                                )
                              }
                            />
                            <Permission
                              permission="View single analysis history"
                              isChecked={
                                documentAnalysisPermissions?.viewSingleLookup
                              }
                              onChange={() =>
                                changeDocumentAnalysisPermissions(
                                  'viewSingleLookup',
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}

                  <Accordion
                    title={
                      <div className="flex items-center">
                        <Checkbox
                          noLeftPadding
                          isChecked={allSettingsPermissions}
                          onChange={() => toggleSettingsPermissions()}
                        />
                        <span className="text-xs font-medium uppercase text-grey">
                          Settings
                        </span>
                      </div>
                    }
                  >
                    <div className="flex flex-col">
                      <div className="py-4 mt-2">
                        <div className="flex flex-col gap-4">
                          <Permission
                            permission="Manage roles and permission"
                            isChecked={settingsPermissions?.managePermissions}
                            onChange={() =>
                              changeSettingsPermissions('managePermissions')
                            }
                          />
                          {teams_services && (
                            <Permission
                              permission="Manage members - invite, edit, and delete"
                              isChecked={settingsPermissions?.manageMembers}
                              onChange={() =>
                                changeSettingsPermissions('manageMembers')
                              }
                            />
                          )}

                          {audit_logs_services && (
                            <Permission
                              permission="View and download Audit logs"
                              isChecked={
                                settingsPermissions?.viewAndDownloadAudit
                              }
                              onChange={() =>
                                changeSettingsPermissions(
                                  'viewAndDownloadAudit',
                                )
                              }
                              disabled={
                                !settingsPermissions.viewAndDownloadAudit
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </Accordion>

                  {developers_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allDeveloperPermissions}
                            onChange={() => toggleDeveloperPermissions()}
                          />
                          <span className="text-xs font-medium uppercase text-grey">
                            Developers
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="py-4 mt-2">
                          <div className="flex flex-col gap-4">
                            <Permission
                              permission="Manage Apps - Create, edit and delete apps"
                              isChecked={developerPermissions?.manageApps}
                              onChange={() =>
                                changeDeveloperPermissions('manageApps')
                              }
                            />
                            <Permission
                              permission="Re-generate keys"
                              isChecked={developerPermissions?.regenerateKeys}
                              onChange={() =>
                                changeDeveloperPermissions('regenerateKeys')
                              }
                            />
                            <Permission
                              permission="View live keys"
                              isChecked={developerPermissions?.viewLiveKeys}
                              onChange={() =>
                                changeDeveloperPermissions('viewLiveKeys')
                              }
                            />
                            {api_logs_services && (
                              <Permission
                                permission="View and export API logs"
                                isChecked={
                                  developerPermissions?.viewAndExportLogs
                                }
                                onChange={() =>
                                  changeDeveloperPermissions(
                                    'viewAndExportLogs',
                                  )
                                }
                              />
                            )}

                            <Permission
                              permission="Create, Edit and delete API tokens"
                              isChecked={developerPermissions?.manageTokens}
                              onChange={() =>
                                changeDeveloperPermissions('manageTokens')
                              }
                            />
                            <Permission
                              permission="Create and edit Webhooks"
                              isChecked={developerPermissions?.manageWebhooks}
                              onChange={() =>
                                changeDeveloperPermissions('manageWebhooks')
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}

                  {aml_screening_services && (
                    <Accordion
                      title={
                        <div className="flex items-center">
                          <Checkbox
                            noLeftPadding
                            isChecked={allAmlScreeningPermissions}
                            onChange={() => {
                              const newOption = !allAmlScreeningPermissions;
                              toggleAmlScreeningPermissions(newOption);
                              toggleUserScreening(newOption);
                              toggleBusinessScreening(newOption);
                            }}
                          />

                          <span className="text-xs font-medium uppercase text-grey">
                            AML screening
                          </span>
                        </div>
                      }
                    >
                      <div className="flex flex-col">
                        <div className="mt-6">
                          <div className="flex items-center">
                            <Checkbox
                              noLeftPadding
                              isChecked={userScreening}
                              onChange={() =>
                                toggleUserScreening(!userScreening)
                              }
                            />
                            <span className="text-xs font-medium uppercase text-grey">
                              User Screening
                            </span>
                          </div>
                          <div className="flex flex-col gap-4 pl-5 mt-4">
                            <Permission
                              permission="Perform single user screening"
                              isChecked={
                                amlScreeningPermissions?.performSingleIndividualLookup
                              }
                              onChange={() =>
                                changeAmlScreeningPermissions(
                                  'performSingleIndividualLookup',
                                )
                              }
                            />
                            <Permission
                              permission="View single user screening lookup history"
                              isChecked={
                                amlScreeningPermissions?.viewSingleIndividualLookup
                              }
                              onChange={() =>
                                changeAmlScreeningPermissions(
                                  'viewSingleIndividualLookup',
                                )
                              }
                            />
                            <Permission
                              permission="Perform batch user screening"
                              isChecked={
                                amlScreeningPermissions?.performBatchIndividualLookup
                              }
                              onChange={() =>
                                changeAmlScreeningPermissions(
                                  'performBatchIndividualLookup',
                                )
                              }
                            />{' '}
                            <Permission
                              permission="View batch user screening lookup history"
                              isChecked={
                                amlScreeningPermissions?.viewBatchIndividualLookup
                              }
                              onChange={() =>
                                changeAmlScreeningPermissions(
                                  'viewBatchIndividualLookup',
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="mt-6">
                          <div className="flex items-center">
                            <Checkbox
                              noLeftPadding
                              isChecked={businessScreening}
                              onChange={() =>
                                toggleBusinessScreening(!businessScreening)
                              }
                            />
                            <span className="text-xs font-medium uppercase text-grey">
                              Business screening
                            </span>
                          </div>
                          <div className="flex flex-col gap-4 pl-5 mt-4">
                            <div className="flex flex-col gap-4">
                              <Permission
                                permission="Perform single business screening"
                                isChecked={
                                  amlScreeningPermissions?.performSingleBusinessLookup
                                }
                                onChange={() =>
                                  changeAmlScreeningPermissions(
                                    'performSingleBusinessLookup',
                                  )
                                }
                              />
                              <Permission
                                permission="View Business single screening lookup history"
                                isChecked={
                                  amlScreeningPermissions?.viewSingleBusinessLookup
                                }
                                onChange={() =>
                                  changeAmlScreeningPermissions(
                                    'viewSingleBusinessLookup',
                                  )
                                }
                              />
                              <Permission
                                permission="Perform batch business screening"
                                isChecked={
                                  amlScreeningPermissions?.performBatchBusinessLookup
                                }
                                onChange={() =>
                                  changeAmlScreeningPermissions(
                                    'performBatchBusinessLookup',
                                  )
                                }
                              />{' '}
                              <Permission
                                permission="View Business batch screening lookup history"
                                isChecked={
                                  amlScreeningPermissions?.viewBatchBusinessLookup
                                }
                                onChange={() =>
                                  changeAmlScreeningPermissions(
                                    'viewBatchBusinessLookup',
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      ) : (
        children
      )}
    </DashboardLayout>
  );
};

export default connect(state => state, Actions)(SettingsTeamLayout);
